import React from 'react';
import { Accordion, Badge, Button, Card, Col, Container, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';

const faqSections = [
  {
    title: 'Ordering & Quotes',
    icon: 'fas fa-shopping-bag',
    items: [
      {
        question: 'How can I place an order?',
        answer:
          'You can place an order directly from our Order Online page. Choose your product, add the required specifications, upload your design if needed, and complete checkout.',
      },
      {
        question: 'Can I get a quotation before ordering?',
        answer:
          'Yes. You can request a quote through the website or contact our team for a customized price estimate based on quantity, size, paper, and finishing options.',
      },
      {
        question: 'Do you support bulk orders?',
        answer:
          'Absolutely. We handle both small and bulk printing jobs with special pricing and production support for larger quantities.',
      },
    ],
  },
  {
    title: 'Artwork & File Upload',
    icon: 'fas fa-file-upload',
    items: [
      {
        question: 'What file formats do you accept?',
        answer:
          'We accept common design formats such as PDF, JPG, PNG, and AI files. For the best print quality, we recommend high-resolution print-ready files.',
      },
      {
        question: 'Can I upload a design after placing my order?',
        answer:
          'Yes. You can upload your design during checkout or send it later through WhatsApp or email if your order is still in processing.',
      },
      {
        question: 'What if my file is low resolution?',
        answer:
          'We will review the uploaded file and may suggest a better resolution or layout adjustments before printing to preserve the final quality.',
      },
    ],
  },
  {
    title: 'Production & Delivery',
    icon: 'fas fa-truck',
    items: [
      {
        question: 'How long does production take?',
        answer:
          'Turnaround time depends on the product type, quantity, and finishing options. We always share a realistic timeline before production begins.',
      },
      {
        question: 'Do you offer delivery across locations?',
        answer:
          'Yes. We provide delivery support for completed orders and can share the delivery estimate based on your location and order size.',
      },
      {
        question: 'Can I track my order status?',
        answer:
          'You can view your order history and current status through your account dashboard once the order has been placed.',
      },
    ],
  },
  {
    title: 'Payments & Support',
    icon: 'fas fa-headset',
    items: [
      {
        question: 'What payment methods do you accept?',
        answer:
          'We support secure online payments as well as standard payment methods for business orders. Contact us for custom billing requirements.',
      },
      {
        question: 'What if I need help with my order?',
        answer:
          'Our support team is available to guide you through order placement, artwork adjustments, delivery updates, and any concerns related to your print job.',
      },
      {
        question: 'Can I modify my order after checkout?',
        answer:
          'Changes may be possible before production starts. Please contact us as soon as possible so we can help update your order if available.',
      },
    ],
  },
];

const quickLinks = [
  { title: 'Order Online', icon: 'fas fa-shopping-cart', path: '/order' },
  { title: 'Products', icon: 'fas fa-print', path: '/products' },
  { title: 'Contact Us', icon: 'fas fa-envelope', path: '/contact' },
];

const FAQPage: React.FC = () => {
  return (
    <div className="page-fade d-flex flex-column" style={{ minHeight: 'calc(100vh - 68px)' }}>
      <div style={{ flex: 1 }}>
        <div className="breadcrumb-bar">
          <Link to="/">Home</Link> &gt; FAQ / Help
        </div>

        <section style={{ padding: '60px 5% 40px', background: 'linear-gradient(135deg, #fff5fb 0%, #f8fbff 50%, #ffffff 100%)' }}>
          <Container>
            <Row className="g-4 align-items-center">
              <Col lg={7}>
                <Badge bg="light" text="dark" className="px-3 py-2 mb-3 rounded-pill border" style={{ fontSize: '12px', fontWeight: 700, color: '#e91e8c' }}>
                  <i className="fas fa-question-circle me-2"></i> Frequently Asked Questions
                </Badge>
                <h1 className="display-5 fw-bold text-dark mb-3" style={{ letterSpacing: '-0.6px', lineHeight: 1.15 }}>
                  Everything you need to know about
                  <span style={{ display: 'block', background: 'linear-gradient(90deg, #e91e8c, #3b5bdb)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                    ordering with Prathitha
                  </span>
                </h1>
                <p className="lead text-muted mb-0" style={{ fontSize: '16px', lineHeight: 1.8, maxWidth: '680px' }}>
                  Browse common questions about pricing, artwork, production timelines, delivery, and support so you can place your print order with confidence.
                </p>
              </Col>
              <Col lg={5}>
                <Card className="border-0 shadow-lg rounded-4" style={{ background: 'rgba(255,255,255,0.92)', border: '1px solid #f0dce8' }}>
                  <Card.Body className="p-4">
                    <h4 className="fw-bold text-dark mb-3">Need a quick answer?</h4>
                    <p className="text-muted mb-4" style={{ lineHeight: 1.7 }}>
                      Use these helpful links to jump straight to the service or contact page for faster support.
                    </p>
                    <div className="d-grid gap-2">
                      {quickLinks.map((item) => (
                        <Link key={item.title} to={item.path} className="btn btn-outline-secondary d-flex align-items-center justify-content-between rounded-3 px-3 py-2" style={{ borderColor: '#e5e7eb', color: '#334155' }}>
                          <span className="d-flex align-items-center gap-2">
                            <i className={item.icon} style={{ color: '#e91e8c' }}></i>
                            {item.title}
                          </span>
                          <i className="fas fa-arrow-right"></i>
                        </Link>
                      ))}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Container>
        </section>

        <section style={{ padding: '20px 5% 80px' }}>
          <Container>
            <Row className="g-4">
              {faqSections.map((section) => (
                <Col lg={6} key={section.title}>
                  <Card className="border-0 shadow-sm rounded-4 h-100" style={{ border: '1px solid #eef2f7' }}>
                    <Card.Body className="p-4">
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <div className="d-flex align-items-center justify-content-center rounded-3" style={{ width: '42px', height: '42px', background: 'linear-gradient(135deg, #e91e8c 0%, #3b5bdb 100%)', color: '#fff' }}>
                          <i className={section.icon}></i>
                        </div>
                        <h3 className="fw-bold text-dark mb-0" style={{ fontSize: '18px' }}>{section.title}</h3>
                      </div>
                      <Accordion alwaysOpen className="faq-accordion">
                        {section.items.map((item, index) => (
                          <Accordion.Item eventKey={`${section.title}-${index}`} key={item.question} className="border-0 mb-2 rounded-3" style={{ background: '#f8fafc' }}>
                            <Accordion.Header style={{ fontSize: '14px' }}>{item.question}</Accordion.Header>
                            <Accordion.Body style={{ color: '#475569', lineHeight: 1.7, fontSize: '14px' }}>
                              {item.answer}
                            </Accordion.Body>
                          </Accordion.Item>
                        ))}
                      </Accordion>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>

            <div className="text-center mt-5">
              <Card className="border-0 shadow-sm rounded-4 d-inline-block" style={{ maxWidth: '760px', border: '1px solid #f0dce8' }}>
                <Card.Body className="p-4">
                  <h4 className="fw-bold text-dark mb-2">Still have questions?</h4>
                  <p className="text-muted mb-3" style={{ lineHeight: 1.7 }}>
                    Our team is happy to help with custom printing requirements, urgent orders, or any details you want to confirm before checkout.
                  </p>
                  <Button as={Link} to="/contact" className="rounded-pill px-4 py-2 fw-semibold" style={{ background: 'linear-gradient(90deg, #e91e8c 0%, #3b5bdb 100%)', border: 'none' }}>
                    Contact Support <i className="fas fa-arrow-right ms-2"></i>
                  </Button>
                </Card.Body>
              </Card>
            </div>
          </Container>
        </section>
      </div>
      <Footer />
    </div>
  );
};

export default FAQPage;
